import { useMemo, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import api from '../lib/api';
import {
  ArrowLeft,
  Package,
  DollarSign,
  MapPin,
  BarChart3,
  Megaphone,
  LayoutGrid,
  CheckCircle,
  AlertTriangle,
  Camera,
  ClipboardCheck,
  Tag,
  AlertCircle,
} from 'lucide-react';

interface VisitData {
  id: string;
  store_id: string;
  store_name: string;
  status: string;
  perfect_store_score?: number;
  photos_count: number;
  gondola_photo: boolean;
  checklist_completed: boolean;
  prices_collected: number;
  extra_points: number;
  ruptures_count: number;
  kpis?: {
    presence?: { score: number; weight: number; completed: boolean };
    price?: { score: number; weight: number; completed: boolean };
    extra_point?: { score: number; weight: number; completed: boolean };
    share?: { score: number; weight: number; completed: boolean };
    pos_material?: { score: number; weight: number; completed: boolean };
    planogram?: { score: number; weight: number; completed: boolean };
  };
}

interface KpiConfig {
  key: string;
  label: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
}

const KPI_CONFIGS: KpiConfig[] = [
  {
    key: 'presence',
    label: 'Presenca',
    icon: <Package className="h-5 w-5" />,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
  },
  {
    key: 'price',
    label: 'Preco',
    icon: <DollarSign className="h-5 w-5" />,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
  },
  {
    key: 'extra_point',
    label: 'Ponto Extra',
    icon: <MapPin className="h-5 w-5" />,
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
  },
  {
    key: 'share',
    label: 'Share',
    icon: <BarChart3 className="h-5 w-5" />,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-100',
  },
  {
    key: 'pos_material',
    label: 'Material PDV',
    icon: <Megaphone className="h-5 w-5" />,
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
  },
  {
    key: 'planogram',
    label: 'Planograma',
    icon: <LayoutGrid className="h-5 w-5" />,
    color: 'text-rose-600',
    bgColor: 'bg-rose-100',
  },
];

function ScoreGauge({ score }: { score: number }) {
  const clampedScore = Math.min(100, Math.max(0, score));
  const radius = 70;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (clampedScore / 100) * circumference;

  let strokeColor = '#ef4444'; // red
  let bgGlow = 'shadow-red-200';
  if (clampedScore >= 80) {
    strokeColor = '#22c55e';
    bgGlow = 'shadow-green-200';
  } else if (clampedScore >= 60) {
    strokeColor = '#eab308';
    bgGlow = 'shadow-yellow-200';
  } else if (clampedScore >= 40) {
    strokeColor = '#f97316';
    bgGlow = 'shadow-orange-200';
  }

  let label = 'Critico';
  if (clampedScore >= 80) label = 'Excelente';
  else if (clampedScore >= 60) label = 'Bom';
  else if (clampedScore >= 40) label = 'Regular';

  return (
    <div className={`relative flex items-center justify-center w-48 h-48 mx-auto rounded-full shadow-lg ${bgGlow}`}>
      <svg className="w-full h-full -rotate-90" viewBox="0 0 160 160">
        {/* Background circle */}
        <circle
          cx="80"
          cy="80"
          r={radius}
          fill="none"
          stroke="#f3f4f6"
          strokeWidth="12"
        />
        {/* Score arc */}
        <circle
          cx="80"
          cy="80"
          r={radius}
          fill="none"
          stroke={strokeColor}
          strokeWidth="12"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          style={{ transition: 'stroke-dashoffset 1.2s ease-in-out, stroke 0.5s ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-4xl font-bold text-gray-900">{clampedScore}</span>
        <span className="text-sm text-gray-500 -mt-0.5">pontos</span>
        <span
          className="text-xs font-semibold mt-1 px-2 py-0.5 rounded-full"
          style={{
            color: strokeColor,
            backgroundColor: `${strokeColor}15`,
          }}
        >
          {label}
        </span>
      </div>
    </div>
  );
}

function KpiScoreBar({ score, color }: { score: number; color: string }) {
  const clampedScore = Math.min(100, Math.max(0, score));

  let barColor = 'bg-red-500';
  if (clampedScore >= 80) barColor = 'bg-green-500';
  else if (clampedScore >= 60) barColor = 'bg-yellow-500';
  else if (clampedScore >= 40) barColor = 'bg-orange-500';

  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-700 ${barColor}`}
          style={{ width: `${clampedScore}%` }}
        />
      </div>
      <span className="text-sm font-semibold text-gray-700 w-10 text-right">{clampedScore}%</span>
    </div>
  );
}

export default function VisitScore({ visitId, storeId }: { visitId: string; storeId: string }) {
  const [, setLocation] = useLocation();

  // Fetch visit data with auto-refresh
  const { data: visit, isLoading, error } = useQuery<VisitData>({
    queryKey: ['visit', 'score', visitId],
    queryFn: () => api.get(`/visits/${visitId}`).then(res => res.data.data),
    refetchInterval: 10000,
  });

  // Trigger score calculation on mount
  const calcMutation = useMutation({
    mutationFn: () => api.post(`/perfect-store/calculate/${visitId}`).then(res => res.data),
  });

  useEffect(() => {
    calcMutation.mutate();
  }, [visitId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate local score from visit data
  const calculatedScore = useMemo(() => {
    if (!visit) return 0;
    if (visit.perfect_store_score !== undefined && visit.perfect_store_score !== null) {
      return visit.perfect_store_score;
    }

    // Calculate from KPIs if available
    if (visit.kpis) {
      const kpiEntries = Object.values(visit.kpis).filter(Boolean);
      if (kpiEntries.length === 0) return 0;

      let totalWeightedScore = 0;
      let totalWeight = 0;

      for (const kpi of kpiEntries) {
        if (kpi) {
          totalWeightedScore += kpi.score * kpi.weight;
          totalWeight += kpi.weight;
        }
      }

      return totalWeight > 0 ? Math.round(totalWeightedScore / totalWeight) : 0;
    }

    return 0;
  }, [visit]);

  // Determine pending items
  const pendingItems = useMemo(() => {
    if (!visit) return [];

    const items: { id: string; label: string; icon: React.ReactNode }[] = [];

    if (!visit.gondola_photo) {
      items.push({
        id: 'gondola',
        label: 'Falta foto de gondola',
        icon: <Camera className="h-5 w-5 text-amber-600" />,
      });
    }

    if (!visit.checklist_completed) {
      items.push({
        id: 'checklist',
        label: 'Checklist incompleto',
        icon: <ClipboardCheck className="h-5 w-5 text-amber-600" />,
      });
    }

    if (visit.prices_collected === 0) {
      items.push({
        id: 'prices',
        label: 'Precos nao coletados',
        icon: <DollarSign className="h-5 w-5 text-amber-600" />,
      });
    }

    if (visit.extra_points === 0) {
      items.push({
        id: 'extra_points',
        label: 'Pontos extras nao registrados',
        icon: <Tag className="h-5 w-5 text-amber-600" />,
      });
    }

    if (visit.photos_count === 0) {
      items.push({
        id: 'photos',
        label: 'Nenhuma foto enviada',
        icon: <Camera className="h-5 w-5 text-amber-600" />,
      });
    }

    if (visit.kpis) {
      const kpiMap: Record<string, string> = {
        presence: 'Presenca nao verificada',
        share: 'Share nao analisado',
        planogram: 'Planograma nao conferido',
        pos_material: 'Material PDV nao verificado',
      };

      for (const [key, label] of Object.entries(kpiMap)) {
        const kpi = visit.kpis[key as keyof typeof visit.kpis];
        if (kpi && !kpi.completed) {
          items.push({
            id: `kpi_${key}`,
            label,
            icon: <AlertCircle className="h-5 w-5 text-amber-600" />,
          });
        }
      }
    }

    return items;
  }, [visit]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando score...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !visit) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
        <AlertCircle className="h-16 w-16 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Erro ao carregar score</h2>
        <p className="text-gray-600 mb-6 text-center">
          Nao foi possivel carregar os dados da visita
        </p>
        <button
          onClick={() => setLocation(`/visit/${storeId}`)}
          className="px-6 py-3 bg-primary text-white rounded-lg font-semibold"
        >
          Voltar para visita
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-primary text-white p-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setLocation(`/visit/${storeId}`)}
            className="p-2 -ml-2 rounded-full hover:bg-white/20"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold">Score da Visita</h1>
            {visit.store_name && (
              <p className="text-sm opacity-80">{visit.store_name}</p>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 p-4 pb-8 space-y-4">
        {/* Main Score Gauge */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <ScoreGauge score={calculatedScore} />
          <p className="text-center text-xs text-gray-400 mt-3">
            Atualizado automaticamente a cada 10 segundos
          </p>
        </div>

        {/* KPI Breakdown */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900">Indicadores (KPIs)</h2>
          </div>

          <div className="divide-y divide-gray-100">
            {KPI_CONFIGS.map(kpiConfig => {
              const kpiData = visit.kpis?.[kpiConfig.key as keyof typeof visit.kpis];
              const score = kpiData?.score ?? 0;
              const weight = kpiData?.weight ?? 0;
              const completed = kpiData?.completed ?? false;

              return (
                <div key={kpiConfig.key} className="p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`p-2 rounded-full ${kpiConfig.bgColor}`}>
                      <span className={kpiConfig.color}>{kpiConfig.icon}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 text-sm">{kpiConfig.label}</span>
                        <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                          Peso: {weight}
                        </span>
                      </div>
                    </div>
                    {completed ? (
                      <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                    ) : (
                      <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0" />
                    )}
                  </div>
                  <KpiScoreBar score={score} color={kpiConfig.color} />
                </div>
              );
            })}
          </div>
        </div>

        {/* Pending Items */}
        {pendingItems.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                <h2 className="font-semibold text-gray-900">Itens Pendentes</h2>
                <span className="text-xs font-semibold text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full ml-auto">
                  {pendingItems.length}
                </span>
              </div>
            </div>

            <div className="divide-y divide-gray-100">
              {pendingItems.map(item => (
                <div
                  key={item.id}
                  className="flex items-center gap-3 p-4 bg-amber-50/50"
                >
                  <div className="p-2 rounded-full bg-amber-100 flex-shrink-0">
                    {item.icon}
                  </div>
                  <span className="text-sm font-medium text-amber-800">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* All complete message */}
        {pendingItems.length === 0 && (
          <div className="bg-green-50 rounded-xl border border-green-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-green-100">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="font-semibold text-green-800">Tudo completo!</p>
                <p className="text-sm text-green-700">Todas as etapas da visita foram realizadas</p>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
